import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Send,
  EyeOff,
  Loader2,
  Eye as EyeIcon,
} from "lucide-react";
import { usePortalAuth } from "../lib/portalAuth";
import { OW_API as ADMIN_API } from "../lib/operatorWalk";
import RichTextEditor from "./RichTextEditor";

function slugifyClient(text) {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const EMPTY = { title: "", slug: "", excerpt: "", body: "", status: "draft" };

export default function AdminBlogEditor({ postId, onBack, onSaved }) {
  const { authHeader } = usePortalAuth();
  const isNew = !postId;
  const [form, setForm] = useState(EMPTY);
  const [original, setOriginal] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(null); // 'draft' | 'publish' | 'update' | 'unpublish'
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${ADMIN_API}/admin/blog/${postId}`, { headers: authHeader })
      .then((res) => {
        if (cancelled) return;
        const d = res.data;
        const next = {
          title: d.title || "",
          slug: d.slug || "",
          excerpt: d.excerpt || "",
          body: d.body || "",
          status: d.status || "draft",
          publishedAt: d.publishedAt || null,
        };
        setForm(next);
        setOriginal(next);
        setSlugTouched(true); // existing slug should not auto-overwrite
      })
      .catch(() => toast.error("Failed to load post"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [postId, authHeader, isNew]);

  // Auto-slug from title until user edits the slug field manually
  useEffect(() => {
    if (slugTouched) return;
    setForm((f) => ({ ...f, slug: slugifyClient(f.title) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title, slugTouched]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return false;
    }
    if (!form.body || form.body.trim() === "" || form.body === "<p></p>") {
      toast.error("Body cannot be empty.");
      return false;
    }
    return true;
  };

  const save = useCallback(
    async (mode) => {
      if (!validate()) return;
      setSaving(mode);
      try {
        const payload = {
          title: form.title.trim(),
          slug: form.slug.trim() || undefined,
          excerpt: form.excerpt.trim(),
          body: form.body,
        };
        if (mode === "draft") payload.status = "draft";
        if (mode === "publish") payload.status = "published";
        if (mode === "unpublish") payload.status = "draft";

        let res;
        if (isNew) {
          res = await axios.post(`${ADMIN_API}/admin/blog`, payload, { headers: authHeader });
        } else {
          // For "update" mode preserve the existing status
          if (mode === "update") delete payload.status;
          res = await axios.patch(`${ADMIN_API}/admin/blog/${postId}`, payload, {
            headers: authHeader,
          });
        }
        const successMessages = {
          draft: "Saved as draft.",
          publish: "Published.",
          update: "Updated.",
          unpublish: "Unpublished.",
        };
        toast.success(successMessages[mode]);
        onSaved?.(res.data);
      } catch (err) {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Save failed.");
      } finally {
        setSaving(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, isNew, postId, authHeader, onSaved]
  );

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center text-ink-muted">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading
      </div>
    );
  }

  const isPublished = form.status === "published";

  return (
    <div data-testid="admin-blog-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-sm text-brand-700 hover:bg-brand-100 px-3 py-2 transition-colors"
            data-testid="admin-blog-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div>
            <div className="eyebrow mb-1">{isNew ? "New Post" : "Edit Post"}</div>
            <h2 className="font-serif text-2xl text-brand-700 leading-tight">
              {form.title || "Untitled"}
            </h2>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowPreview((v) => !v)}
            className="inline-flex items-center gap-2 border border-brand-700 text-brand-700 hover:bg-brand-100 px-4 py-2.5 text-sm font-medium transition-colors"
            data-testid="admin-blog-preview-toggle"
          >
            <EyeIcon className="w-4 h-4" /> {showPreview ? "Edit" : "Preview"}
          </button>
          {isNew || !isPublished ? (
            <>
              <button
                onClick={() => save("draft")}
                disabled={!!saving}
                className="inline-flex items-center gap-2 border border-brand-700 text-brand-700 hover:bg-brand-100 px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
                data-testid="admin-blog-save-draft"
              >
                {saving === "draft" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save as Draft
              </button>
              <button
                onClick={() => save("publish")}
                disabled={!!saving}
                className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
                data-testid="admin-blog-publish"
              >
                {saving === "publish" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Publish
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => save("unpublish")}
                disabled={!!saving}
                className="inline-flex items-center gap-2 border border-brand-700 text-brand-700 hover:bg-brand-100 px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
                data-testid="admin-blog-unpublish"
              >
                {saving === "unpublish" ? <Loader2 className="w-4 h-4 animate-spin" /> : <EyeOff className="w-4 h-4" />}
                Unpublish
              </button>
              <button
                onClick={() => save("update")}
                disabled={!!saving}
                className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
                data-testid="admin-blog-update"
              >
                {saving === "update" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Update
              </button>
            </>
          )}
        </div>
      </div>

      {showPreview ? (
        <div className="border border-ink-line bg-white p-8 lg:p-12" data-testid="admin-blog-preview">
          <h1 className="font-serif text-4xl lg:text-5xl text-brand-700 leading-tight mb-6">
            {form.title || "Untitled"}
          </h1>
          {form.excerpt && (
            <p className="text-lg text-ink-soft mb-8 italic">{form.excerpt}</p>
          )}
          <div className="prose-blog" dangerouslySetInnerHTML={{ __html: form.body }} />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">
                Title
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                placeholder="What did you learn this week?"
                className="w-full border border-ink-line bg-white px-4 py-3 text-lg text-ink focus:outline-none focus:border-brand-700 transition-colors font-serif"
                data-testid="admin-blog-input-title"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">
                Body
              </label>
              <RichTextEditor
                value={form.body}
                onChange={(html) => setField("body", html)}
                placeholder="Start writing..."
              />
            </div>
          </div>
          <aside className="lg:col-span-4 space-y-5">
            <div className="border border-ink-line bg-brand-50/40 p-5">
              <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500 mb-3">
                Status
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center px-2 py-1 text-[10px] uppercase tracking-[0.15em] ${
                    isPublished
                      ? "bg-brand-700 text-white"
                      : "bg-white text-brand-700 border border-brand-200"
                  }`}
                  data-testid="admin-blog-status-badge"
                >
                  {form.status}
                </span>
                {form.publishedAt && isPublished && (
                  <span className="text-xs text-ink-muted">
                    {new Date(form.publishedAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">
                Slug
              </label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setField("slug", slugifyClient(e.target.value));
                }}
                placeholder="auto-generated-from-title"
                className="w-full border border-ink-line bg-white px-4 py-3 text-sm text-ink focus:outline-none focus:border-brand-700 transition-colors font-mono"
                data-testid="admin-blog-input-slug"
              />
              <p className="mt-2 text-xs text-ink-muted">
                URL: /blog/{form.slug || "your-post"}
              </p>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">
                Excerpt
                <span className="ml-2 normal-case tracking-normal text-ink-muted">
                  (optional, auto-generated from body)
                </span>
              </label>
              <textarea
                value={form.excerpt}
                onChange={(e) => setField("excerpt", e.target.value)}
                rows={4}
                placeholder="One or two sentences. Shown on the blog index and used as the meta description."
                className="w-full border border-ink-line bg-white px-4 py-3 text-sm text-ink focus:outline-none focus:border-brand-700 transition-colors resize-y"
                data-testid="admin-blog-input-excerpt"
              />
              <p className="mt-2 text-xs text-ink-muted">
                {(form.excerpt || "").length} / 500
              </p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
